/**
 * SOLEMATE MVP — API Client
 *
 * Provides typed access to the Hono backend API.
 * Automatically handles JWT injection from localStorage.
 */

const BASE_URL = ''; // Proxied via Vite config to localhost:8787 in dev, empty in prod (served by same Worker)

export interface User {
  id: string;
  wallet: string;
  username: string | null;
  display_name: string | null;
  bio: string | null;
  avatar_key: string | null;
  is_creator: number;
  tip_wallet: string | null;
  created_at: string;
}

export interface Post {
  id: string;
  creator_id: string;
  caption: string | null;
  media_key: string | null;
  tier: 'free' | 'bronze' | 'silver' | 'gold' | 'diamond';
  like_count: number;
  view_count: number;
  created_at: string;
  creator_name: string;
  creator_avatar: string | null;
  creator_wallet: string;
  locked?: boolean;
  required_tier?: 'free' | 'bronze' | 'silver' | 'gold' | 'diamond';
  viewer_tier?: 'free' | 'bronze' | 'silver' | 'gold' | 'diamond';
}

export interface CreatorStats {
  follower_count: number;
  post_count: number;
  total_tips: number;
}

export interface CreatorDetail {
  creator: User;
  stats: CreatorStats;
}

export interface Tip {
  id: string;
  from_wallet: string;
  amount_tokens: number;
  message: string | null;
  tx_sig: string;
  created_at: string;
  from_display_name: string | null;
  from_avatar: string | null;
}

export interface TipperLeaderboardEntry {
  from_wallet: string;
  from_user_id: string | null;
  display_name: string | null;
  avatar_key: string | null;
  total_amount: number;
  tip_count: number;
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const jwt = localStorage.getItem('solemate_jwt');
  const headers = new Headers(options.headers || {});
  
  if (jwt) {
    headers.set('Authorization', `Bearer ${jwt}`);
  }

  const response = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorMessage = `HTTP Error ${response.status}`;
    try {
      const errorData = await response.json() as { error?: string };
      errorMessage = errorData.error || errorMessage;
    } catch {
      // ignore JSON parse error
    }
    throw new Error(errorMessage);
  }

  return response.json() as Promise<T>;
}

export const api = {
  /** Get a random UUID challenge from the server for signing */
  async challenge(): Promise<{ nonce: string }> {
    return request<{ nonce: string }>('/api/auth/challenge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
  },

  /** Verify the signature and return token + user profile */
  async verify(wallet: string, signature: string, nonce: string): Promise<{ token: string; user: User }> {
    const data = await request<{ token: string; user: User }>('/api/auth/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ wallet, signature, nonce }),
    });
    localStorage.setItem('solemate_jwt', data.token);
    return data;
  },

  /** Get the current user profile from JWT */
  async me(): Promise<{ user: User }> {
    return request<{ user: User }>('/api/auth/me');
  },

  /** Clear local JWT storage */
  logout(): void {
    localStorage.removeItem('solemate_jwt');
  },

  /** Get list of creators (ordered by followers/recency) */
  async getCreators(page = 1, limit = 20): Promise<{ creators: (User & { follower_count: number })[]; page: number; limit: number }> {
    return request<{ creators: (User & { follower_count: number })[]; page: number; limit: number }>(
      `/api/creators?page=${page}&limit=${limit}`
    );
  },

  /** Get specific creator profile + stats */
  async getCreator(id: string): Promise<CreatorDetail> {
    return request<CreatorDetail>(`/api/creators/${id}`);
  },

  /** Apply to become a creator */
  async applyCreator(tip_wallet?: string): Promise<{ user: User }> {
    return request<{ user: User }>('/api/creators/apply', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tip_wallet }),
    });
  },

  /** Update creator bio, avatar, display name */
  async updateProfile(data: { display_name?: string; bio?: string; avatar_key?: string }): Promise<{ user: User }> {
    return request<{ user: User }>('/api/creators/profile', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /** Follow or unfollow a creator */
  async followCreator(id: string): Promise<{ followed: boolean }> {
    return request<{ followed: boolean }>(`/api/creators/${id}/follow`, {
      method: 'POST',
    });
  },

  /** Get posts feed (filtered by followed creators or global fallback) */
  async getPosts(page = 1, limit = 20): Promise<{ posts: Post[]; page: number; limit: number }> {
    return request<{ posts: Post[]; page: number; limit: number }>(`/api/posts/feed?page=${page}&limit=${limit}`);
  },

  /** Create a new post */
  async createPost(data: { caption?: string; media_key: string; tier?: string }): Promise<{ post: Post }> {
    return request<{ post: Post }>('/api/posts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /** Get specific post detail (to evaluate token gate) */
  async getPost(id: string): Promise<{ post: Post; locked: boolean; required_tier?: string; viewer_tier?: string }> {
    return request<{ post: Post; locked: boolean; required_tier?: string; viewer_tier?: string }>(`/api/posts/${id}`);
  },

  /** Delete a post */
  async deletePost(id: string): Promise<{ deleted: boolean }> {
    return request<{ deleted: boolean }>(`/api/posts/${id}`, {
      method: 'DELETE',
    });
  },

  /** Toggle like on a post */
  async likePost(id: string): Promise<{ liked: boolean }> {
    return request<{ liked: boolean }>(`/api/posts/${id}/like`, {
      method: 'POST',
    });
  },

  /**
   * Upload media file (image/video) to R2.
   * Send the file as raw binary in request body.
   */
  async uploadMedia(file: File): Promise<{ key: string }> {
    return request<{ key: string }>('/api/media/upload', {
      method: 'POST',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    });
  },

  /** Record a verified SPL token transfer tip */
  async tipCreator(data: { tx_sig: string; to_creator_id: string; message?: string }): Promise<{
    tip: {
      id: string;
      amount: number;
      platform_fee: number;
      creator_receives: number;
      tx_sig: string;
    };
  }> {
    return request<{
      tip: {
        id: string;
        amount: number;
        platform_fee: number;
        creator_receives: number;
        tx_sig: string;
      };
    }>('/api/tips', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /** Get top tippers leaderboard (last 7 days) */
  async getTopTippers(limit = 20): Promise<{ leaderboard: TipperLeaderboardEntry[] }> {
    return request<{ leaderboard: TipperLeaderboardEntry[] }>(`/api/tips/top?limit=${limit}`);
  },

  /** Get tips received by a specific creator */
  async getCreatorTips(id: string, page = 1, limit = 20): Promise<{
    tips: Tip[];
    total_received: number;
    total_count: number;
    page: number;
    limit: number;
  }> {
    return request<{
      tips: Tip[];
      total_received: number;
      total_count: number;
      page: number;
      limit: number;
    }>(`/api/tips/creator/${id}?page=${page}&limit=${limit}`);
  },
  
  /** Helper to get absolute media URL (respecting token gates) */
  getMediaUrl(key: string, postId?: string): string {
    const suffix = postId ? `?post_id=${postId}` : '';
    const jwt = localStorage.getItem('solemate_jwt');
    const authParam = jwt ? `&token=${encodeURIComponent(jwt)}` : '';
    let url = `${BASE_URL}/api/media/${key}${suffix}`;
    if (authParam) {
      url += suffix ? authParam : `?${authParam.slice(1)}`;
    }
    return url;
  },

  async fetchMediaBlob(key: string, postId?: string): Promise<string> {
    const jwt = localStorage.getItem('solemate_jwt');
    const headers = new Headers();
    if (jwt) {
      headers.set('Authorization', `Bearer ${jwt}`);
    }
    const suffix = postId ? `?post_id=${postId}` : '';
    const response = await fetch(`${BASE_URL}/api/media/${key}${suffix}`, {
      headers,
    });
    if (!response.ok) {
      throw new Error(`Failed to load media: ${response.statusText}`);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }
};
